<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card'
import {
  ArrowLeft,
  Mail,
  Calendar,
  Crown,
  Sparkles,
  Star,
  Play,
  CheckCircle,
  Clock,
  Users,
  User,
  CreditCard,
  Video,
  FileText,
  XCircle,
  AlertCircle,
} from 'lucide-vue-next'

interface Subscription {
  id: number
  tier_name: string
  tier_slug: string
  amount: number
  status: string
  status_display: string
  current_period_start: string | null
  current_period_end: string | null
  created_at: string
  cancelled_at: string | null
  cancellation_reason: string | null
  selected_slot: {
    day?: string
    time?: string
    subject_name?: string
    level_name?: string
  } | null
}

interface Student {
  id: number
  name: string
  email: string
  avatar_url: string | null
  subscribed_at: string
  subscriptions: Subscription[]
  active_subscription: {
    id: number
    tier_name: string
    tier_slug: string
    amount: number
    current_period_end: string | null
  } | null
}

interface Activity {
  id: number
  content_id: number
  content_title: string
  content_type: string
  thumbnail_url: string | null
  duration_minutes: number | null
  progress: number
  is_completed: boolean
  last_watched: string
}

interface Session {
  id: number
  schedule_name: string
  schedule_type: string
  enrollment_type: string
  occurrence_date: string | null
  status: string
  attended: boolean
  teacher_notes: string | null
}

interface Payment {
  id: number
  amount: number
  currency: string
  status: string
  status_display: string
  tier_name: string
  paid_at: string | null
  created_at: string
}

const props = defineProps<{
  student: Student
  activity: Activity[]
  sessions: Session[]
  payments: Payment[]
}>()

const activeTab = ref<'activity' | 'sessions' | 'payments'>('activity')

const tabs = [
  { id: 'activity', label: 'Activity', icon: Play },
  { id: 'sessions', label: 'Sessions', icon: Users },
  { id: 'payments', label: 'Payments', icon: CreditCard },
]

function getTierIcon(tierSlug: string) {
  switch (tierSlug) {
    case 'gold':
      return Crown
    case 'silver':
      return Sparkles
    default:
      return Star
  }
}

function getTierColor(tierSlug: string): string {
  switch (tierSlug) {
    case 'gold':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    case 'silver':
      return 'text-gray-600 bg-gray-100 border-gray-200'
    default:
      return 'text-blue-600 bg-blue-50 border-blue-200'
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'text-green-700 bg-green-50'
    case 'cancelled':
      return 'text-red-700 bg-red-50'
    case 'expired':
      return 'text-gray-700 bg-gray-100'
    case 'past_due':
      return 'text-orange-700 bg-orange-50'
    default:
      return 'text-gray-700 bg-gray-100'
  }
}

function getPaymentStatusColor(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'text-green-700 bg-green-50'
    case 'failed':
      return 'text-red-700 bg-red-50'
    case 'pending':
      return 'text-yellow-700 bg-yellow-50'
    default:
      return 'text-gray-700 bg-gray-100'
  }
}

function formatCurrency(amount: number, currency: string = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

function formatSlot(slot: Subscription['selected_slot']): string {
  if (!slot) return ''
  const parts = []
  if (slot.day) parts.push(slot.day)
  if (slot.time) parts.push(slot.time)
  if (slot.subject_name) parts.push(slot.subject_name)
  return parts.join(' - ')
}

function getContentTypeIcon(type: string) {
  switch (type) {
    case 'video':
      return Video
    default:
      return FileText
  }
}

const completedCount = computed(() => props.activity.filter(a => a.is_completed).length)
const totalWatchTime = computed(() => {
  return props.activity.reduce((acc, a) => {
    if (a.duration_minutes && a.progress) {
      return acc + Math.round((a.duration_minutes * a.progress) / 100)
    }
    return acc
  }, 0)
})

const totalPaid = computed(() => {
  return props.payments
    .filter(p => p.status === 'succeeded')
    .reduce((acc, p) => acc + p.amount, 0)
})
</script>

<template>
  <Head :title="`${student.name} - Student Details`" />

  <DashboardLayout>
    <div class="w-full max-w-6xl space-y-6">
      <!-- Back Button -->
      <Link
        href="/teacher/students"
        class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <ArrowLeft class="w-4 h-4" />
        Back to Students
      </Link>

      <!-- Student Header -->
      <Card>
        <CardContent class="pt-6">
          <div class="grid grid-cols-[max-content_1fr] md:flex items-start gap-6">
            <!-- Avatar -->
            <div class="shrink-0">
              <img
                v-if="student.avatar_url"
                :src="student.avatar_url"
                :alt="student.name"
                class="w-20 h-20 rounded-full object-cover"
              />
              <div v-else class="w-20 h-20 rounded-full bg-linear-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-2xl">
                {{ student.name.charAt(0).toUpperCase() }}
              </div>
            </div>

            <!-- Info -->
            <div class="flex-1">
              <div class="flex items-center gap-3 mb-2">
                <h1 class="text-xl font-bold text-gray-900">{{ student.name }}</h1>
                <span
                  v-if="student.active_subscription"
                  :class="[getTierColor(student.active_subscription.tier_slug), 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border']"
                >
                  <component :is="getTierIcon(student.active_subscription.tier_slug)" class="w-4 h-4" />
                  {{ student.active_subscription.tier_name }}
                </span>
                <span v-else class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs md:text-sm font-medium text-gray-500 bg-gray-100 border border-gray-200">
                  Inactive
                </span>
              </div>

              <div class="flex flex-col md:flex-col md:items-center gap-3 md: text-sm text-gray-500 mb-4">
                <span class="flex items-center gap-1.5">
                  <Mail class="w-4 h-4" />
                  {{ student.email }}
                </span>
                <span class="flex items-center gap-1.5">
                  <Calendar class="w-4 h-4" />
                  Student since {{ student.subscribed_at }}
                </span>
              </div>

              <!-- Active Subscription Details -->
              <div v-if="student.active_subscription" class="flex items-center gap-4 text-sm">
                <span class="text-gray-600">
                  <strong>{{ formatCurrency(student.active_subscription.amount) }}</strong>/week
                </span>
                <span v-if="student.active_subscription.current_period_end" class="text-gray-500">
                  Renews {{ student.active_subscription.current_period_end }}
                </span>
              </div>
            </div>

            <!-- Quick Stats -->
            <div class="shrink-0 grid grid-cols-3 gap-4 text-center col-span-2">
              <div class="bg-gray-50 rounded-lg px-4 py-3">
                <div class="text-2xl font-bold text-gray-900">{{ activity.length }}</div>
                <div class="text-xs text-gray-500">Videos</div>
              </div>
              <div class="bg-gray-50 rounded-lg px-4 py-3">
                <div class="text-2xl font-bold text-green-600">{{ completedCount }}</div>
                <div class="text-xs text-gray-500">Completed</div>
              </div>
              <div class="bg-gray-50 rounded-lg px-4 py-3">
                <div class="text-2xl font-bold text-gray-900">{{ sessions.length }}</div>
                <div class="text-xs text-gray-500">Sessions</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Subscription History -->
      <Card v-if="student.subscriptions.length > 0">
        <CardHeader>
          <CardTitle class="text-base">Subscription History</CardTitle>
        </CardHeader>
        <CardContent>
          <div class="space-y-3">
            <div
              v-for="sub in student.subscriptions"
              :key="sub.id"
              class="flex flex-col md:flex-row items-center justify-between gap-4  py-3 border-b border-gray-100 last:border-0"
            >
              <div class="flex items-center gap-3">
                <span :class="[getStatusColor(sub.status), 'px-2 py-1 rounded text-xs font-medium']">
                  {{ sub.status_display }}
                </span>
                <span class="font-medium text-gray-900">{{ sub.tier_name }}</span>
                <span class="text-sm text-gray-500">{{ formatCurrency(sub.amount) }}/week</span>
              </div>
              <div class="text-sm text-gray-500 flex items-center gap-2 md:`gap-4">
                <span v-if="sub.selected_slot && formatSlot(sub.selected_slot)" class="flex items-center gap-1">
                  <Clock class="w-3.5 h-3.5" />
                  {{ formatSlot(sub.selected_slot) }}
                </span>
                <span>{{ sub.created_at }}</span>
                <span v-if="sub.current_period_end">to {{ sub.current_period_end }}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Tabs -->
      <div class="border-b border-gray-200">
        <nav class="flex overflow-y-auto gap-8 pb-4">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id as 'activity' | 'sessions' | 'payments'"
            :class="[
              'flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            <component :is="tab.icon" class="w-4 h-4" />
            {{ tab.label }}
            <span
              v-if="tab.id === 'activity'"
              class="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full"
            >
              {{ activity.length }}
            </span>
            <span
              v-if="tab.id === 'sessions'"
              class="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full"
            >
              {{ sessions.length }}
            </span>
            <span
              v-if="tab.id === 'payments'"
              class="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full"
            >
              {{ payments.length }}
            </span>
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div>
        <!-- Activity Tab -->
        <div v-if="activeTab === 'activity'">
          <Card>
            <CardHeader>
              <CardTitle>Content Activity</CardTitle>
              <CardDescription>
                {{ completedCount }} completed, ~{{ totalWatchTime }} min watched
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div v-if="activity.length === 0" class="text-center py-12 text-gray-400">
                <Play class="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p class="font-medium text-gray-600">No activity yet</p>
                <p class="text-sm">This student hasn't watched any content yet.</p>
              </div>

              <div v-else class="divide-y divide-gray-100">
                <div
                  v-for="item in activity"
                  :key="item.id"
                  class="flex items-center gap-4 py-4"
                >
                  <!-- Thumbnail -->
                  <div class="shrink-0 w-24 h-14 bg-gray-100 rounded-lg overflow-hidden relative">
                    <img
                      v-if="item.thumbnail_url"
                      :src="item.thumbnail_url"
                      :alt="item.content_title"
                      class="w-full h-full object-cover"
                    />
                    <div v-else class="w-full h-full flex items-center justify-center">
                      <component :is="getContentTypeIcon(item.content_type)" class="w-6 h-6 text-gray-400" />
                    </div>
                    <div v-if="item.is_completed" class="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                      <CheckCircle class="w-3 h-3 text-white" />
                    </div>
                  </div>

                  <!-- Info -->
                  <div class="flex-1 min-w-0">
                    <h4 class="font-medium text-gray-900 truncate">{{ item.content_title }}</h4>
                    <div class="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span v-if="item.duration_minutes" class="flex items-center gap-1">
                        <Clock class="w-3.5 h-3.5" />
                        {{ item.duration_minutes }} min
                      </span>
                      <span>Last watched {{ item.last_watched }}</span>
                    </div>
                  </div>

                  <!-- Progress -->
                  <div class="shrink-0 w-32">
                    <div class="flex items-center justify-between text-sm mb-1">
                      <span class="text-gray-500">Progress</span>
                      <span :class="item.is_completed ? 'text-green-600 font-medium' : 'text-gray-900'">
                        {{ item.progress }}%
                      </span>
                    </div>
                    <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        :class="item.is_completed ? 'bg-green-500' : 'bg-blue-500'"
                        :style="{ width: `${item.progress}%` }"
                        class="h-full transition-all"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <!-- Sessions Tab -->
        <div v-if="activeTab === 'sessions'">
          <Card>
            <CardHeader>
              <CardTitle>Sessions</CardTitle>
              <CardDescription>Group and 1:1 session history</CardDescription>
            </CardHeader>
            <CardContent>
              <div v-if="sessions.length === 0" class="text-center py-12 text-gray-400">
                <Users class="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p class="font-medium text-gray-600">No sessions yet</p>
                <p class="text-sm">This student hasn't attended any sessions yet.</p>
              </div>

              <div v-else class="divide-y divide-gray-100">
                <div
                  v-for="session in sessions"
                  :key="session.id"
                  class="flex items-center justify-between py-4"
                >
                  <div class="flex items-center gap-4">
                    <div :class="[
                      'w-10 h-10 rounded-lg flex items-center justify-center',
                      session.enrollment_type === 'group' ? 'bg-purple-100' : 'bg-blue-100'
                    ]">
                      <component
                        :is="session.enrollment_type === 'group' ? Users : User"
                        :class="session.enrollment_type === 'group' ? 'text-purple-600' : 'text-blue-600'"
                        class="w-5 h-5"
                      />
                    </div>
                    <div>
                      <h4 class="font-medium text-gray-900">{{ session.schedule_name }}</h4>
                      <div class="text-sm text-gray-500">
                        {{ session.enrollment_type === 'group' ? 'Group Session' : '1:1 Session' }}
                        <span v-if="session.occurrence_date"> - {{ session.occurrence_date }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center gap-3">
                    <span
                      v-if="session.attended"
                      class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-green-700 bg-green-50"
                    >
                      <CheckCircle class="w-3 h-3" />
                      Attended
                    </span>
                    <span
                      v-else-if="session.status === 'cancelled'"
                      class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-red-700 bg-red-50"
                    >
                      <XCircle class="w-3 h-3" />
                      Cancelled
                    </span>
                    <span
                      v-else
                      class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-gray-700 bg-gray-100"
                    >
                      {{ session.status }}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <!-- Payments Tab -->
        <div v-if="activeTab === 'payments'">
          <Card>
            <CardHeader>
              <div class="flex items-center justify-between">
                <div>
                  <CardTitle>Payment History</CardTitle>
                  <CardDescription>Total paid: {{ formatCurrency(totalPaid) }}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div v-if="payments.length === 0" class="text-center py-12 text-gray-400">
                <CreditCard class="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p class="font-medium text-gray-600">No payments yet</p>
                <p class="text-sm">Payment records will appear here once processed.</p>
              </div>

              <div v-else class="divide-y divide-gray-100">
                <div
                  v-for="payment in payments"
                  :key="payment.id"
                  class="flex items-center justify-between py-4"
                >
                  <div class="flex items-center gap-4">
                    <div :class="[
                      'w-10 h-10 rounded-lg flex items-center justify-center',
                      payment.status === 'succeeded' ? 'bg-green-100' : payment.status === 'failed' ? 'bg-red-100' : 'bg-gray-100'
                    ]">
                      <CreditCard :class="[
                        'w-5 h-5',
                        payment.status === 'succeeded' ? 'text-green-600' : payment.status === 'failed' ? 'text-red-600' : 'text-gray-600'
                      ]" />
                    </div>
                    <div>
                      <h4 class="font-medium text-gray-900">{{ payment.tier_name }}</h4>
                      <div class="text-sm text-gray-500">
                        {{ payment.paid_at || payment.created_at }}
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center gap-4">
                    <span :class="[getPaymentStatusColor(payment.status), 'px-2 py-1 rounded text-xs font-medium']">
                      {{ payment.status_display }}
                    </span>
                    <span class="font-semibold text-gray-900">
                      {{ formatCurrency(payment.amount, payment.currency) }}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>
